/**
 * Responsibility: the search filters, behind one button.
 *
 * Laid out like the collection's filter button because it is the same
 * control solving the same problem, and two pages that invent two
 * arrangements of one idea is how an app stops feeling like one app.
 *
 * **Why they moved behind a button.** Seven fields across the top of the
 * page wrapped onto two rows on a laptop, and those two rows plus the query
 * box plus the figures left the results about a row and a half of window.
 * The filters are the least-used thing up there and were taking the most
 * room.
 *
 * **Never silently.** The risk of hiding a filter is a bound left on from
 * last week making a search look like it found nothing. So the button
 * carries the number of active filters and turns solid when there are any,
 * and clearing them is one click from the same place.
 */
import Badge from '@mui/material/Badge';
import Button from '@mui/material/Button';
import Popover from '@mui/material/Popover';
import Stack from '@mui/material/Stack';
import FilterIcon from '@mui/icons-material/FilterAltOutlined';
import ClearIcon from '@mui/icons-material/FilterAltOffOutlined';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { SearchFilterFields } from './SearchFilterFields';
import { activeFilterCount, withoutFilters } from './searchDraft';
import type { SearchDraft } from './searchDraft';

type SearchFilterButtonProps = {
  draft: SearchDraft;
  onChange: (next: SearchDraft) => void;
  /** Applied when the popover closes, so a change takes effect. */
  onApply: () => void;
  busy: boolean;
};

export function SearchFilterButton({ draft, onChange, onApply, busy }: SearchFilterButtonProps) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const active = activeFilterCount(draft);

  /**
   * Closing applies, but only when a search has already been run.
   *
   * A filter is not a question on its own - narrowing nothing returns
   * nothing - so before the first search this just closes, and the fields
   * ride along with the phrase when it is submitted.
   */
  const close = () => {
    setAnchor(null);
    if (draft.q.trim().length >= 2) onApply();
  };

  return (
    <>
      <Badge badgeContent={active} color="primary">
        <Button
          size="small"
          variant={active === 0 ? 'outlined' : 'contained'}
          startIcon={<FilterIcon />}
          onClick={(event) => setAnchor(event.currentTarget)}
          sx={{ whiteSpace: 'nowrap' }}
        >
          Filters
        </Button>
      </Badge>

      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { p: 2, maxWidth: 560 } } }}
      >
        <Stack spacing={1.5}>
          <SearchFilterFields draft={draft} onChange={onChange} />

          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
              {active === 0 ? 'Nothing narrowed' : `${active} narrowing this search`}
            </Typography>
            <Button
              size="small"
              variant="text"
              startIcon={<ClearIcon />}
              disabled={active === 0 || busy}
              onClick={() => onChange(withoutFilters(draft))}
            >
              Clear
            </Button>
            <Button size="small" variant="contained" onClick={close} disabled={busy}>
              Done
            </Button>
          </Stack>
        </Stack>
      </Popover>
    </>
  );
}
